import clsx from "clsx";
import RadioInput from "../RadioInput/RadioInput";
import CheckBox from "../CheckBox/CheckBox";
import {
  CSSProperties,
  startTransition,
  useEffect,
  useMemo,
  useState,
} from "react";
import { find } from "lodash";
import useMobile from "src/hooks/useMobile";
import { useSwipeable } from "react-swipeable";
import { ALLOW_SWIPE_IN_DESKTOP } from "src/helpers/constants";

const SWIPE_DELTA = 150;

export type CheckBoxGridType = {
  onChange: (selected: any[]) => void;
  selected: any[];
  disabled?: boolean;
  options: Array<any>;
  columns: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
};

const CheckBoxGrid: React.FC<CheckBoxGridType> = ({
  disabled = false,
  options,
  columns = 2,
  selected,
  onChange,
  onSwipeLeft,
  onSwipeRight,
}) => {
  const { isMobile } = useMobile();
  const [translateX, setTranslateX] = useState<number>(0);

  const [selectedOptions, setSelectedOptions] = useState<any[]>(selected || []);

  const isSwipeable = useMemo(() => {
    return (
      !!onSwipeLeft &&
      !!onSwipeRight &&
      (isMobile ? true : ALLOW_SWIPE_IN_DESKTOP)
    );
  }, [onSwipeLeft, onSwipeRight, isMobile]);

  const handlers = useSwipeable({
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: true,
    onSwiping: ({ deltaX, dir }) => {
      if (dir === "Left" || dir === "Right") {
        startTransition(() => {
          setTranslateX(deltaX);
        });
      }
    },
    onSwiped: (eventData) => {
      const { dir, deltaX } = eventData;
      if (["Left", "Right"].includes(dir)) {
        startTransition(() => {
          if (Math.abs(deltaX) > SWIPE_DELTA) {
            if (dir === "Left") onSwipeLeft?.();
            if (dir === "Right") onSwipeRight?.();
          }
          setTranslateX(0);
        });
      }
    },
  });

  useEffect(() => {
    onChange(selectedOptions);
  }, [selectedOptions]);

  return (
    <div
      className={clsx([
        "grid",
        "gap-2",
        "w-full",
        `grid-cols-${columns}`,
        {
          "pointer-events-none": disabled,
          "cursor-grab": isSwipeable,
        },
      ])}
      {...(isSwipeable ? handlers : {})}
      style={{
        ...((isSwipeable
          ? {
              touchAction: "pan-y",
              transform: `translateX(${translateX}px)`,
              transition: translateX === 0 ? "transform 0.2s ease" : "none",
            }
          : {}) as CSSProperties),
      }}
    >
      {options?.map?.(({ label, description, value }) => {
        return (
          <CheckBox
            label={label}
            description={description}
            checked={find(selectedOptions, { value })}
            key={value}
            onChange={(isChecked: boolean) => {
              if (isChecked) {
                setSelectedOptions((prev: any) => [...prev, { value, label }]);
              } else {
                setSelectedOptions((prev: any) =>
                  prev.filter?.(
                    (selectedValue: any) => selectedValue?.value !== value,
                  ),
                );
              }
            }}
          />
        );
      })}
    </div>
  );
};

export default CheckBoxGrid;
